import GroundStoryCard from '@/components/ProductCard/GroundStoryCard';
import { useStoriesInfiniteQuery } from '@/hooks/RQHooks/story/useStoriesInfiniteQuery';
import { useViewportStore } from '@/store/useViewportStore';
import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import SectionTitle from '../common/SectionTitle';
type Story = {
  id: number;
  date: string;
  count: number;
  title: string;
  thumbnailUrl: string;
};

export default function GroundStoryCarousel() {
  const { data } = useStoriesInfiniteQuery();
  const router = useRouter();
  const stories = useMemo(() => data?.pages.flatMap((page) => page.result.content) ?? [], [data]);
  const isMobile = useViewportStore((store) => store.isMobile);

  const isOneRow = !isMobile ? stories.length <= 3 : false;

  const [curSlideIndex, setCurSlideIndex] = useState(0);

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true }, [
    Autoplay({ delay: 5000, stopOnInteraction: false }),
  ]);

  const storyGroups = useMemo(() => {
    const groupSize = isMobile ? 2 : 3;
    return stories.reduce((acc, story, index) => {
      const groupIndex = Math.floor(index / groupSize);
      if (!acc[groupIndex]) {
        acc[groupIndex] = [];
      }
      acc[groupIndex].push({
        id: story.id,
        date: story.startDate,
        count: story.viewCount,
        title: story.title,
        thumbnailUrl: story.thumbnailUrl,
      });
      return acc;
    }, [] as Story[][]);
  }, [stories, isMobile]);

  const handleCardClick = (id: number) => () => {
    router.push(`/story/${id}`);
  };

  const handleDotClick = (index: number) => {
    emblaApi?.scrollTo(index);
  };

  useEffect(() => {
    if (emblaApi) {
      emblaApi.on('select', () => {
        setCurSlideIndex(emblaApi.selectedScrollSnap());
      });
    }
  }, [emblaApi]);

  return (
    <Container>
      <SectionTitle title={'루플 스토리'} slogan={'여기서만 들을 수 있는 농어촌 이야기'} />
      <CarouselWrapper>
        <Embla ref={emblaRef}>
          <EmblaContainer>
            {storyGroups.map((group, index) => (
              <EmblaSlide key={index}>
                <GridContainer isOneRow={isOneRow}>
                  {group.map((item) => (
                    <GroundStoryCard
                      key={item.id}
                      {...item}
                      handleClick={handleCardClick(item.id)}
                    />
                  ))}
                </GridContainer>
              </EmblaSlide>
            ))}
          </EmblaContainer>
        </Embla>
      </CarouselWrapper>
      {/* 하단 점 (indicator) */}
      {storyGroups && stories.length > 1 && (
        <DotsWrapper>
          {storyGroups.map((_, idx) => (
            <Dot
              key={idx}
              isActive={curSlideIndex === idx}
              onClick={(e) => {
                e.stopPropagation();
                handleDotClick(idx);
              }}
            />
          ))}
        </DotsWrapper>
      )}
    </Container>
  );
}

const Container = styled.div`
  margin-top: 80px;
  width: 100%;
`;

const CarouselWrapper = styled.div`
  overflow: hidden;
  width: 100%;
`;

const Embla = styled.div`
  overflow: hidden;
  flex: 1;
`;

const EmblaContainer = styled.div`
  display: flex;
`;

const EmblaSlide = styled.div`
  display: flex;
  flex: 0 0 100%;
  justify-content: space-around;
`;

const DotsWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 6px;
  z-index: 2;
`;

const Dot = styled.button<{ isActive: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ isActive, theme }) =>
    isActive ? theme.colors.primary : theme.colors.gray300};
  border: none;
  cursor: pointer;
  transition: background-color 0.3s;
`;

const GridContainer = styled.div<{ isOneRow?: boolean }>`
  display: ${({ isOneRow }) => (isOneRow ? 'flex' : 'grid')};
  gap: 16px;
  width: 100%;
  justify-items: center;

  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); /* 기본 PC 크기 */

  @media (max-width: 1024px) {
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr); /* 태블릿에서는 2개 */
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr); /* 모바일에서도 2개 유지 */
    gap: 5px;
  }

  @media (max-width: 375px) {
    grid-template-columns: repeat(2, minmax(130px, 1fr)); /* 375px 이하에서는 1개 */
  }
`;
